import type {
  ArsenicRisk,
  HardnessCategory,
  ResolvedReport,
  WaterProfile,
} from "./types";

/** One flat shape the report copy/render consume, derived from any resolver layer (or null). */
export interface WaterView {
  profile: WaterProfile;
  displayName: string;
  cp: string;
  /** True only when we have concrete locality numbers (drives the precise punch/causes). */
  hasLocalityData: boolean;
  hardnessMgL: number | null;
  hardnessCat: HardnessCategory;
  chlorineMin: number | null;
  chlorineMax: number | null;
  arsenicRisk: ArsenicRisk;
  arsenicUgL: number | null;
}

function mid(a: number | null, b: number | null): number | null {
  if (a != null && b != null) return Math.round((a + b) / 2);
  return a ?? b ?? null;
}

export function toWaterView(report: ResolvedReport | null, rawCp: string): WaterView {
  if (!report) {
    return {
      profile: "F",
      displayName: "tu zona",
      cp: rawCp,
      hasLocalityData: false,
      hardnessMgL: null,
      hardnessCat: "desconocida",
      chlorineMin: null,
      chlorineMax: null,
      arsenicRisk: "desconocido",
      arsenicUgL: null,
    };
  }
  const base = { profile: report.profile, displayName: report.resolvedName, cp: report.cp };

  if (report.locality) {
    const l = report.locality;
    return {
      ...base,
      hasLocalityData: true,
      hardnessMgL: l.hardness_mg_l_caco3,
      hardnessCat: l.hardness_category,
      chlorineMin: l.chlorine_mg_l_min,
      chlorineMax: l.chlorine_mg_l_max,
      arsenicRisk: l.arsenic_risk,
      arsenicUgL: l.arsenic_ug_l_typical,
    };
  }
  if (report.zone) {
    const z = report.zone;
    return {
      ...base,
      hasLocalityData: false,
      hardnessMgL: mid(z.typical_hardness_mg_l_caco3_min, z.typical_hardness_mg_l_caco3_max),
      hardnessCat: z.hardness_category,
      chlorineMin: null,
      chlorineMax: null,
      arsenicRisk: z.arsenic_risk,
      arsenicUgL: mid(z.typical_arsenic_ug_l_min, z.typical_arsenic_ug_l_max),
    };
  }
  // province (or any layer without concrete water data)
  return {
    ...base,
    hasLocalityData: false,
    hardnessMgL: null,
    hardnessCat: "desconocida",
    chlorineMin: null,
    chlorineMax: null,
    arsenicRisk: "desconocido",
    arsenicUgL: null,
  };
}
